// config/dealFieldConfig.ts
import type { FormFieldConfig } from "../components/common/FormField";

export const dealFieldConfig: {
  section: string;
  columns: number;
  column: number;
  fields: FormFieldConfig[];
}[] = [
  {
    section: "Overview",
    column: 0,
    columns: 2,
    fields: [
      { field: "deal_name", label: "Deal Name", type: "text" },
      { field: "deal_name_alias", label: "Deal Name Alias", type: "text" },
      {
        field: "status",
        label: "Status",
        type: "select",
        optionLabelKey: "status",
        optionsKey: "status",
      },
      {
        field: "countries",
        label: "Countries",
        type: "multi-select",
        optionLabelKey: "nicename",
        optionsKey: "countries",
      },
      {
        field: "sectors",
        label: "Sectors",
        type: "multi-select",
        optionLabelKey: "sector",
        optionsKey: "sectors",
      },
      {
        field: "loan_amount_currency",
        label: "Currency",
        type: "select",
        optionsKey: "currencies",
        isStringArray: true,
      },
      {
        field: "source_entity",
        label: "Source",
        type: "autocomplete",
        optionLabelKey: "source_name",
        optionsKey: "sources",
      },
      {
        field: "initial_loan_term",
        label: "Initial Loan Term (y)",
        type: "number",
      },
      { field: "full_loan_term", label: "Full Loan Term (y)", type: "number" },
      { field: "margin", label: "Margin (%)", type: "number" },
      { field: "rate_floor", label: "Rate Floor (%)", type: "number" },
      { field: "probability", label: "Probability (%)", type: "number" },
      { field: "priority", label: "Priority", type: "text" },
      { field: "amortisation", label: "Amortisation?", type: "checkbox" },
      { field: "is_portfolio", label: "Is Portfolio?", type: "checkbox" },
      {
        field: "amortisation_amount",
        label: "Amortisation (p.a)",
        type: "number",
        formatNumber: true,
      },
      {
        field: "is_development_loan",
        label: "Is Development?",
        type: "checkbox",
      },
      {
        field: "amortisation_percentage",
        label: "Amortisation (%)",
        type: "number",
      },
      { field: "is_funded", label: "Funded?", type: "checkbox" },
      {
        field: "has_complete_term_sheet",
        label: "Term Sheet Completed?",
        type: "checkbox",
      },
      {
        field: "has_complete_ten_pager",
        label: "Ten Pager Completed?",
        type: "checkbox",
      },
      { field: "box_folder_id", label: "Box Folder ID", type: "number" },
      { field: "cm_rating", label: "CM Rating", type: "text" },
      { field: "wal", label: "WAL", type: "number" },
      { field: "target_closing", label: "Expected Closing Date", type: "date" },
      { field: "proposed_ic_date", label: "IC Date", type: "date" },
      { field: "irr_local", label: "IRR Local (%)", type: "number" },
      { field: "irr_usd", label: "IRR USD (%)", type: "number" },
      {
        field: "irr_local_net",
        label: "IRR Local (Post TenFifty Fees)",
        type: "number",
      },
      {
        field: "irr_usd_net",
        label: "IRR USD (Post TenFifty Fees)",
        type: "number",
      },
    ],
  },
  {
    section: "Total Commitments",
    column: 1,
    columns: 2,
    fields: [
      {
        field: "loan_amount_day_one",
        label: "Loan Amount Day 1",
        type: "number",
        formatNumber: true,
      },
      {
        field: "loan_amount_fully_funded",
        label: "Loan Amount FF",
        type: "number",
        formatNumber: true,
      },
      { field: "ltv", label: "LTV (%)", type: "number" },
      { field: "ltc", label: "LTC (%)", type: "number" },
      { field: "dy", label: "DY (%)", type: "number" },
      { field: "icr", label: "ICR (%)", type: "number" },
      { field: "dscr", label: "DSCR (%)", type: "number" },
    ],
  },
  {
    section: "Acquisition Facility",
    column: 1,
    columns: 2,
    fields: [
      {
        field: "acq_fac_loan_amount_day_one",
        label: "Loan Amount Day 1",
        type: "number",
        formatNumber: true,
      },
      {
        field: "acq_fac_loan_amount_fully_funded",
        label: "Loan Amount FF",
        type: "number",
        formatNumber: true,
      },
      { field: "acq_fac_ltpp", label: "LTPP (%)", type: "number" },
      { field: "acq_fac_dy", label: "DY (%)", type: "number" },
      { field: "acq_fac_icr", label: "ICR (%)", type: "number" },
      { field: "acq_fac_dscr", label: "DSCR (%)", type: "number" },
      {
        field: "acq_fac_arrangement_fee",
        label: "Arrangement Fee (%)",
        type: "number",
      },
    ],
  },
  {
    section: "Development Facility",
    column: 1,
    columns: 2,
    fields: [
      {
        field: "dev_fac_loan_amount_day_one",
        label: "Loan Amount Day 1",
        type: "number",
        formatNumber: true,
      },
      {
        field: "dev_fac_loan_amount_fully_funded",
        label: "Loan Amount FF",
        type: "number",
        formatNumber: true,
      },
      { field: "dev_fac_ltc", label: "LTC (%)", type: "number" },
      { field: "dev_fac_ltv", label: "LTV (%)", type: "number" },
      { field: "dev_fac_ltgdv", label: "LTGDV (%)", type: "number" },
      {
        field: "dev_fac_arrangement_fee",
        label: "Arrangement Fee (%)",
        type: "number",
      },
    ],
  },
  {
    section: "Capex Facility",
    column: 1,
    columns: 2,
    fields: [
      {
        field: "capex_fac_loan_amount_day_one",
        label: "Loan Amount Day 1",
        type: "number",
        formatNumber: true,
      },
      {
        field: "capex_fac_loan_amount_fully_funded",
        label: "Loan Amount FF",
        type: "number",
        formatNumber: true,
      },
      { field: "capex_fac_ltc", label: "LTC (%)", type: "number" },
      {
        field: "capex_fac_pt_capex_cost",
        label: "% of Capex Costs",
        type: "number",
      },
      {
        field: "capex_fac_arrangement_fee",
        label: "Arrangement Fee (%)",
        type: "number",
      },
    ],
  },
  {
    section: "Interest Reserve Facility",
    column: 1,
    columns: 2,
    fields: [
      {
        field: "int_res_fac_loan_amount_day_one",
        label: "Loan Amount Day 1",
        type: "number",
        formatNumber: true,
      },
      {
        field: "int_res_fac_pt_cap_int_commitfees",
        label: "% of Capitalised Interest & Commitment Fees",
        type: "number",
      },
      {
        field: "int_res_fac_arrangement_fee",
        label: "Arrangement Fee (%)",
        type: "number",
      },
    ],
  },
  {
    section: "VAT Facility",
    column: 1,
    columns: 2,
    fields: [
      {
        field: "vat_fac_loan_amount_day_one",
        label: "Loan Amount Day 1",
        type: "number",
        formatNumber: true,
      },
      {
        field: "vat_fac_loan_amount_fully_funded",
        label: "Loan Amount FF",
        type: "number",
        formatNumber: true,
      },
      {
        field: "vat_fac_percent_vat_covered",
        label: "% VAT Covered",
        type: "number",
      },
      {
        field: "vat_fac_arrangement_fee",
        label: "Arrangement Fee (%)",
        type: "number",
      },
    ],
  },
  {
    section: "Sponsor & Borrowers",
    column: 2,
    columns: 1,
    fields: [
      {
        field: "sponsor",
        label: "Sponsor",
        type: "autocomplete",
        optionLabelKey: "sponsor_name",
        optionsKey: "sponsors",
      },
      {
        field: "borrowers",
        label: "Borrowers",
        type: "Borrowers",
        optionLabelKey: "borrower_name",
        optionsKey: "borrowers",
      },
      {
        field: "secondary_sponsor",
        label: "Secondary Sponsor / Manager",
        type: "autocomplete",
        optionLabelKey: "sponsor_name",
        optionsKey: "sponsors",
      },
      {
        field: "secondary_borrowers",
        label: "Secondary Borrowers",
        type: "Borrowers",
        optionLabelKey: "borrower_name",
        optionsKey: "borrowers",
      },
      {
        field: "equity_contribution",
        label: "Equity Contribution",
        type: "number",
        formatNumber: true,
      },
    ],
  },
  {
    section: "Fees",
    column: 2,
    columns: 2,
    fields: [
      {
        field: "arrangement_fee",
        label: "Arrangement Fee (%)",
        type: "number",
      },
      { field: "commitment_fee", label: "Commitment Fee (%)", type: "number" },
      {
        field: "make_whole_fee_interestmonths",
        label: "Make Whole Fee (months of interest)",
        type: "number",
      },
      {
        field: "facility_agent_fee",
        label: "Facility Agent Fee",
        type: "number",
        formatNumber: true,
      },
      { field: "exit_fee", label: "Exit Fee (%)", type: "number" },
      { field: "extension_fee", label: "Extension Fee (%)", type: "number" },
    ],
  },
  {
    section: "Agents",
    column: 2,
    columns: 1,
    fields: [
      {
        field: "facility_agent_name",
        label: "Facility Agent Name",
        type: "text",
      },
      {
        field: "security_agent_name",
        label: "Security Agent Name",
        type: "text",
      },
    ],
  },
  {
    section: "TenFifty Fees",
    column: 2,
    columns: 2,
    fields: [
      {
        field: "tf_arrangement_fee",
        label: "TenFifty Arrangement Fee (%)",
        type: "number",
      },
      {
        field: "tf_servicing_fee",
        label: "TenFifty Servicing Fee (%)",
        type: "number",
      },
    ],
  },
  {
    section: "Specifics",
    column: 0,
    columns: 2,
    fields: [
      {
        field: "deal_lead",
        label: "Deal Lead",
        type: "select",
        optionLabelKey: "name",
        optionsKey: "users",
      },
      {
        field: "deal_support",
        label: "Deal Support",
        type: "select",
        optionLabelKey: "name",
        optionsKey: "users",
      },
      {
        field: "loan_type",
        label: "Loan Type",
        type: "select",
        optionLabelKey: "loan_type",
        optionsKey: "loanTypes",
      },
      {
        field: "kill_reason",
        label: "Kill Reason",
        type: "select",
        optionLabelKey: "kill_reason",
        optionsKey: "killReasons",
      },
      {
        field: "risk_class",
        label: "Risk Class",
        type: "select",
        optionLabelKey: "risk_class",
        optionsKey: "riskClasses",
      },
      {
        field: "security_type",
        label: "Security Type",
        type: "select",
        optionLabelKey: "security_type",
        optionsKey: "securityTypes",
      },
      {
        field: "transaction_type",
        label: "Transaction Type",
        type: "select",
        optionLabelKey: "transaction_type",
        optionsKey: "transactionTypes",
      },
      {
        field: "capital_source",
        label: "Capital Source",
        type: "select",
        optionLabelKey: "name",
        optionsKey: "capitalSources",
      },
    ],
  },
  {
    section: "Financial Covenants",
    column: 3,
    columns: 3,
    fields: [
      { field: "covenant_dscr", label: "DSCR (%)", type: "number" },
      { field: "covenant_icr", label: "ICR (%)", type: "number" },
      { field: "covenant_ltv", label: "LTV (%)", type: "number" },
      { field: "covenant_ltc", label: "LTC (%)", type: "number" },
      { field: "covenant_dy", label: "DY (%)", type: "number" },
    ],
  },
  {
    section: "Financial Covenants by Year",
    column: 3,
    columns: 3,
    fields: [
      // only
      { field: "covenant_ltv_years", label: "LTV (%)", type: "year-based" },
      { field: "covenant_ltc_years", label: "LTC (%)", type: "year-based" },
      { field: "covenant_icr_years", label: "ICR (%)", type: "year-based" },
      { field: "covenant_dscr_years", label: "DSCR (%)", type: "year-based" },
      { field: "covenant_dy_years", label: "DY (%)", type: "year-based" },
    ],
  },
  {
    section: "Cash Trap events",
    column: 3,
    columns: 3,
    fields: [
      { field: "cashtrap_dscr", label: "DSCR (%)", type: "number" },
      { field: "cashtrap_icr", label: "ICR (%)", type: "number" },
      { field: "cashtrap_ltv", label: "LTV (%)", type: "number" },
      { field: "cashtrap_ltc", label: "LTC (%)", type: "number" },
      { field: "cashtrap_dy", label: "DY (%)", type: "number" },
    ],
  },
  {
    section: "Cash Trap Events by Year",
    column: 3,
    columns: 3,
    fields: [
      { field: "cashtrap_ltv_years", label: "LTV (%)", type: "year-based" },
      { field: "cashtrap_ltc_years", label: "LTC (%)", type: "year-based" },
      { field: "cashtrap_icr_years", label: "ICR (%)", type: "year-based" },
      { field: "cashtrap_dscr_years", label: "DSCR (%)", type: "year-based" },
      { field: "cashtrap_dy_years", label: "DY (%)", type: "year-based" },
    ],
  },

  {
    section: "Extension Conditions",
    column: 3,
    columns: 3,
    fields: [
      { field: "extension_condition_dscr", label: "DSCR (%)", type: "number" },
      { field: "extension_condition_icr", label: "ICR (%)", type: "number" },
      { field: "extension_condition_ltv", label: "LTV (%)", type: "number" },
      { field: "extension_condition_ltc", label: "LTC (%)", type: "number" },
      { field: "extension_condition_dy", label: "DY (%)", type: "number" },
      {
        field: "extension_condition_occupancy",
        label: "Occupancy (%)",
        type: "number",
      },
    ],
  },
];
